import { useState, useEffect, useMemo } from 'react';
import { format, subDays } from 'date-fns';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';
import { PLATFORM_COLORS } from '../components/analytics/constants';

/**
 * Central data hook for the Analytics page.
 * Owns all state and API calls so the page component stays lean.
 */
const useAnalyticsData = () => {
  const [analytics, setAnalytics] = useState(null);
  const [topPosts, setTopPosts] = useState([]);
  const [bestTimes, setBestTimes] = useState(null);
  const [audience, setAudience] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState('all');
  const [dateRange, setDateRange] = useState('30');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [platformHealth, setPlatformHealth] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Post performance state
  const [postsPerformance, setPostsPerformance] = useState(null);
  const [postsSortBy, setPostsSortBy] = useState('engagement');
  const [postsSortOrder, setPostsSortOrder] = useState('desc');
  const [postsLoading, setPostsLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postDetail, setPostDetail] = useState(null);
  const [postDetailLoading, setPostDetailLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCalendar, dateRange]);

  useEffect(() => {
    if (activeTab === 'posts') {
      fetchPostsPerformance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, postsSortBy, postsSortOrder, selectedCalendar]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCalendar !== 'all') params.append('calendar_id', selectedCalendar);
      params.append('start_date', format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd'));
      params.append('end_date', format(new Date(), 'yyyy-MM-dd'));

      const [analyticsRes, calendarsRes, topPostsRes, bestTimesRes, audienceRes, healthRes] = await Promise.all([
        api.get(`/analytics/overview?${params.toString()}`),
        api.get('/calendars'),
        api.get(`/analytics/top-posts?${params.toString()}`),
        api.get(`/analytics/best-times?${params.toString()}`),
        api.get(`/analytics/audience?${params.toString()}`),
        api.get('/analytics/health'),
      ]);

      setAnalytics(analyticsRes.data);
      setCalendars(calendarsRes.data);
      setTopPosts(topPostsRes.data.top_posts || []);
      setBestTimes(bestTimesRes.data);
      setAudience(audienceRes.data);
      setPlatformHealth(healthRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPostsPerformance = async () => {
    setPostsLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCalendar !== 'all') params.append('calendar_id', selectedCalendar);
      params.append('sort_by', postsSortBy);
      params.append('order', postsSortOrder);
      params.append('limit', '50');

      const res = await api.get(`/analytics/posts/performance?${params.toString()}`);
      setPostsPerformance(res.data);
    } catch (error) {
      console.error('Error fetching posts performance:', error);
      toast.error('Failed to load post performance data');
    } finally {
      setPostsLoading(false);
    }
  };

  const fetchPostDetail = async (postId) => {
    setPostDetailLoading(true);
    setSelectedPost(postId);
    try {
      const res = await api.get(`/analytics/posts/${postId}/detailed`);
      setPostDetail(res.data);
    } catch (error) {
      console.error('Error fetching post details:', error);
      toast.error('Failed to load post details');
    } finally {
      setPostDetailLoading(false);
    }
  };

  const toggleSortOrder = () => setPostsSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));

  const handleExportAnalytics = () => {
    if (!analytics) {
      toast.error('No analytics data to export');
      return;
    }
    const rows = [
      ['StelliSocial Analytics Export'],
      ['Date Range', `Last ${dateRange} days`],
      ['Generated', new Date().toISOString()],
      [],
      ['Platform', 'Posts', 'Reach', 'Engagement', 'Clicks', 'Impressions'],
    ];
    if (analytics.platform_breakdown) {
      Object.entries(analytics.platform_breakdown).forEach(([platform, data]) => {
        rows.push([platform, data.posts || 0, data.reach || 0, data.engagement || 0, data.clicks || 0, data.impressions || 0]);
      });
    }
    rows.push([]);
    rows.push(['Summary']);
    rows.push(['Total Posts', analytics.summary?.total_posts || 0]);
    rows.push(['Total Reach', analytics.summary?.total_reach || 0]);
    rows.push(['Total Engagement', analytics.summary?.total_engagement || 0]);
    rows.push(['Engagement Rate', `${analytics.summary?.engagement_rate || 0}%`]);

    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_${dateRange}d_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success('Analytics exported as CSV');
  };

  const handleRefreshAnalytics = async () => {
    setRefreshing(true);
    try {
      const calParam = selectedCalendar !== 'all' ? `?calendar_id=${selectedCalendar}` : '';
      await api.post(`/analytics/refresh${calParam}`);
      toast.success('Analytics refresh started. Data will update shortly.');
      setTimeout(() => {
        fetchData();
        setRefreshing(false);
      }, 3000);
    } catch (error) {
      toast.error('Failed to trigger analytics refresh');
      setRefreshing(false);
    }
  };

  const getDataSourceLabel = () => {
    if (!platformHealth) return { label: 'Loading...', color: 'text-slate-500' };
    const s = platformHealth.summary;
    if (s.overall_status === 'all_healthy') return { label: 'Live data from connected platforms', color: 'text-emerald-600' };
    if (s.overall_status === 'mixed') return { label: `${s.healthy} live, ${s.demo} simulated`, color: 'text-blue-600' };
    if (s.overall_status === 'demo_only') return { label: 'Demo data — connect accounts for real analytics', color: 'text-amber-600' };
    if (s.overall_status === 'needs_attention') return { label: 'Some data is stale — refresh recommended', color: 'text-amber-600' };
    return { label: 'Connect social accounts for real analytics', color: 'text-slate-500' };
  };

  // Derived / memoized data
  const platformData = useMemo(
    () =>
      analytics?.platform_breakdown
        ? Object.entries(analytics.platform_breakdown).map(([platform, data]) => ({
            name: platform.charAt(0).toUpperCase() + platform.slice(1),
            value: data.engagement,
            posts: data.posts,
            reach: data.reach,
            clicks: data.clicks || 0,
            impressions: data.impressions || 0,
            color: PLATFORM_COLORS[platform] || '#94a3b8',
          }))
        : [],
    [analytics?.platform_breakdown]
  );

  const pieData = useMemo(
    () => platformData.map((p) => ({ name: p.name, value: p.reach, color: p.color })),
    [platformData]
  );

  return {
    // Data
    analytics,
    topPosts,
    bestTimes,
    audience,
    calendars,
    platformHealth,
    platformData,
    pieData,
    postsPerformance,
    postDetail,
    selectedPost,
    // Loading flags
    loading,
    refreshing,
    postsLoading,
    postDetailLoading,
    // Filters
    selectedCalendar,
    setSelectedCalendar,
    dateRange,
    setDateRange,
    activeTab,
    setActiveTab,
    // Sort
    postsSortBy,
    setPostsSortBy,
    postsSortOrder,
    toggleSortOrder,
    // Actions
    fetchPostDetail,
    handleExportAnalytics,
    handleRefreshAnalytics,
    getDataSourceLabel,
  };
};

export default useAnalyticsData;
